import React, { useState, useEffect } from 'react';

/**
 * Editable numeric input that pairs with a Slider.
 * - Shows the raw numeric value (easy to type)
 * - Optional prefix (e.g. ₹) and suffix (e.g. %, yrs)
 * - Clamps to [min, max] on blur
 * - Keeps in sync when the Slider updates the parent value
 */
const ValueInput = ({
  value,
  onChange,
  min,
  max,
  prefix = '',
  suffix = '',
  colorCls = 'text-amber-600',
  testId,
  width = 'w-24 sm:w-32',
}) => {
  const [raw, setRaw] = useState(String(value));

  useEffect(() => {
    setRaw(String(value));
  }, [value]);

  const commit = (text) => {
    const cleaned = String(text).replace(/[^0-9.-]/g, '');
    const n = parseFloat(cleaned);
    if (isNaN(n)) {
      setRaw(String(value));
      return;
    }
    const clamped = Math.max(min, Math.min(max, n));
    onChange(clamped);
    setRaw(String(clamped));
  };

  return (
    <div className="flex items-center gap-1">
      {prefix && (
        <span className={`text-sm sm:text-base font-bold ${colorCls}`}>{prefix}</span>
      )}
      <input
        type="text"
        inputMode="decimal"
        data-testid={testId}
        value={raw}
        onChange={(e) => {
          const v = e.target.value;
          setRaw(v);
          const cleaned = v.replace(/[^0-9.-]/g, '');
          const n = parseFloat(cleaned);
          if (!isNaN(n)) {
            onChange(Math.max(min, Math.min(max, n)));
          }
        }}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            commit(e.currentTarget.value);
            e.currentTarget.blur();
          }
        }}
        className={`${width} h-9 px-2 text-right text-base sm:text-lg font-bold ${colorCls} bg-white border-2 border-slate-200 rounded-lg focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-200`}
      />
      {suffix && (
        <span className={`text-sm sm:text-base font-bold ${colorCls}`}>{suffix}</span>
      )}
    </div>
  );
};

export default ValueInput;
